import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { equals } from "ramda";

/**
 * What the user is looking at, declared by the page itself.
 *
 * Pages supply facts, not prose: the wording lives in `formatPageContext` so
 * every page reads the same way and can be reworded in one place.
 */
export type PageContext =
  | { page: "home" }
  | { page: "profile" }
  | { page: "agents" }
  | { page: "datasets" }
  | { page: "projects" }
  | { page: "kb" }
  | { page: "admin" }
  | { page: "article"; title: string; slug: string }
  | { page: "agent"; name: string; id: string }
  | { page: "dataset"; name: string; id: string }
  | { page: "project"; name: string; id: string };

/** The one sentence an agent is told about where the user is. */
export function formatPageContext(context: PageContext): string {
  switch (context.page) {
    case "home":
      return "The user is on the Manifold home page.";
    case "profile":
      return "The user is on their own profile and settings page.";
    case "agents":
      return "The user is on the Agents list, which shows the agents available to them.";
    case "datasets":
      return "The user is on the Datasets list, which shows the datasets they can access.";
    case "projects":
      return "The user is on the Projects list, which shows their projects.";
    case "kb":
      return "The user is on the knowledge base index, which lists the articles you can read with read_article.";
    case "admin":
      return "The user is on an admin page, used for managing users, groups, datasets and platform settings.";
    case "article":
      return `The user is reading the knowledge base article "${context.title}"; read_article with the slug ${context.slug} gives you the same text.`;
    case "agent":
      return `The user is on the page for the agent "${context.name}" (id ${context.id}).`;
    case "dataset":
      return `The user is on the page for the dataset "${context.name}" (id ${context.id}).`;
    case "project":
      return `The user is on the page for the project "${context.name}" (id ${context.id}).`;
  }
}

interface PageContextValue {
  context: PageContext | null;
  setContext: (context: PageContext | null) => void;
}

// Defaulted rather than thrown on, so a page can declare itself without every
// test that renders it having to supply the provider.
const Context = createContext<PageContextValue>({ context: null, setContext: () => {} });

export const PageContextProvider = ({ children }: { children: ReactNode }) => {
  const [context, setContext] = useState<PageContext | null>(null);

  return <Context.Provider value={{ context, setContext }}>{children}</Context.Provider>;
};

/**
 * Declares what page the caller is. Pass `null` while the page's own data is
 * still loading, so a half-formed description never reaches an agent.
 *
 * Callers pass an object literal, so the value is a fresh reference on every
 * render; the contents decide whether anything changed.
 */
export function usePageContext(value: PageContext | null): void {
  const { context, setContext } = useContext(Context);

  useEffect(() => {
    if (!equals(value, context)) setContext(value);
  }, [value, context, setContext]);

  // Clearing belongs to unmount alone — folding it into the effect above would
  // wipe the value on every change.
  useEffect(() => () => setContext(null), [setContext]);
}

/** The sentence to send, or null on a page that has not declared itself. */
export function useCurrentPageContext(): string | null {
  const { context } = useContext(Context);
  return context ? formatPageContext(context) : null;
}
